<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useCreateSensorStore } from '@/store/createSensor'
import InputGroup from '@/components/Auths/InputGroup.vue'
import { SensorType, translateSensorType } from '@/interfaces/enums.ts'

interface Props {
  sensorType: string
  initialName?: string
}

const props = defineProps<Props>()
const emit = defineEmits(['validate'])

const createStore = useCreateSensorStore()
const schemaName = ref(props.initialName || '')
const schemaForm = ref<Record<string, string>>({})

const formattedSensorType = computed(() => {
  if (!props.sensorType) return '-'
  return translateSensorType(props.sensorType as SensorType)
})

const getSystemFields = (type: string) => {
  const electricityFields = [
    { key: 'current_a', label: 'Corrente A' },
    { key: 'current_b', label: 'Corrente B' },
    { key: 'current_c', label: 'Corrente C' },
    { key: 'voltage_a', label: 'Voltagem A' },
    { key: 'voltage_b', label: 'Voltagem B' },
    { key: 'voltage_c', label: 'Voltagem C' },
    { key: 'power_factor', label: 'Fator Pot. Total' },
    { key: 'power_factor_a', label: 'Fator Pot. A' },
    { key: 'power_factor_b', label: 'Fator Pot. B' },
    { key: 'power_factor_c', label: 'Fator Pot. C' },
  ]
  
  if (type === SensorType.TEMPERATURE) return [{ key: 'temperature', label: 'Temperatura' }]
  if (type === SensorType.DOOR_OPEN) return [{ key: 'voltage_field', label: 'Voltagem/Status' }]
  
  return electricityFields
}

const formFields = computed(() => getSystemFields(props.sensorType))
const hasSchemaMapping = computed(() => {
  return Object.values(schemaForm.value).some((v) => v && String(v).trim() !== '')
})
const isValid = computed(() => schemaName.value.trim().length > 0 && hasSchemaMapping.value)

watch([schemaName, schemaForm], () => {
  const cleanSchema: Record<string, string> = {}
  Object.entries(schemaForm.value).forEach(([systemKey, manufacturerKey]) => {
    if (manufacturerKey && manufacturerKey.trim() !== '') {
      cleanSchema[systemKey] = manufacturerKey.trim()
    }
  })

  const payload = {
    name: schemaName.value,
    sensor_type: props.sensorType,
    is_public_schema: false,
    schema: cleanSchema
  }

  createStore.updateDraftSchema(payload)
  emit('validate', isValid.value)
}, { deep: true, immediate: true })

</script>

<template>
  <div class="animate-fadeIn max-w-4xl mx-auto w-full">
    <div class="text-center mb-10">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">Criar Novo Modelo</h2>
      <p class="text-body text-slate-500 dark:text-gray-400 mt-1">
        Defina como os dados de payload do sensor devem ser interpretados.
      </p>
    </div>

    <div class="bg-white border border-stroke dark:border-strokedark dark:bg-boxdark shadow-default">
      <div class="p-6 space-y-8">
        <div class="max-w-xl">
          <InputGroup 
            label="Nome do Modelo" 
            name="new_schema_name" 
            type="text" 
            placeholder="Ex: Padrão data_ds7" 
            v-model="schemaName" 
          />
        </div>

        <div class="border border-stroke dark:border-strokedark">
          <div class="px-6 py-3 bg-gray-50 border-b border-stroke dark:bg-meta-4 dark:border-strokedark">
            <h3 class="text-xs font-bold text-gray-700 uppercase tracking-wider dark:text-gray-300">
              Mapeamento de Chaves (JSON)
            </h3>
          </div>
          <div class="bg-white dark:bg-boxdark p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div v-for="(field, index) in formFields" :key="field.key" class="flex flex-col sm:flex-row sm:items-center justify-between">
                <div class="sm:w-1/2 pr-4 mb-2 sm:mb-0">
                  <span class="text-sm font-medium text-black dark:text-white block">{{ field.label }}</span>
                  <span class="text-xs font-mono text-gray-500 dark:text-gray-400">{{ field.key }}</span>
                </div>
                <div class="sm:w-1/2">
                  <input 
                    type="text" 
                    v-model="schemaForm[field.key]" 
                    class="w-full  border-stroke bg-transparent py-2 px-3 text-sm font-mono text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 text-xs text-gray-500 dark:bg-meta-4 dark:text-gray-400 border-t border-stroke dark:border-strokedark">
            * Deixe em branco os campos que este sensor não envia.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>